/** Port von `AuthService.GetViewPermissionsByViewId` und `AuthService.GetViewByEntity`. */

import type { ChinaAppSysGroup, ChinaAppSysGroupView, ChinaAppSysView } from '@/types/entities';
import type { EntityTypes } from '@/types/enums';

type ViewPermission =
  'AllowAudit' | 'AllowCreate' | 'AllowDelete' | 'AllowEdit' | 'AllowRegiezentrum';

/** `default(DateTime)` so, wie System.Text.Json den Wert serialisiert. */
const DEFAULT_DATE_TIME = '0001-01-01T00:00:00';

/** Pendant zu `Groups?.Any(e => e.IstAdministrativ) ?? false`. */
export function isAdministrator(groups: ChinaAppSysGroup[] | null): boolean {
  return groups?.some((group) => group.IstAdministrativ) ?? false;
}

/**
 * Leeres `ChinaAppSysGroupView` mit additiv berechneten Rechten: Ein Recht gilt, sobald eine
 * Gruppe des Benutzers es für die View gewährt — oder der Benutzer Administrator ist.
 */
export function createViewPermissions(
  groups: ChinaAppSysGroup[] | null,
  viewId: number,
): ChinaAppSysGroupView {
  const administrator = isAdministrator(groups);

  const allow = (permission: ViewPermission): boolean =>
    (groups?.some(
      (group) => group.Views?.some((view) => view.ViewId === viewId && view[permission]) ?? false,
    ) ??
      false) ||
    administrator;

  return {
    Id: 0,
    Created: DEFAULT_DATE_TIME,
    CreatedByUserId: 0,
    Modified: DEFAULT_DATE_TIME,
    ModifiedByUserId: 0,
    Timestamp: null,
    AllowAudit: allow('AllowAudit'),
    AllowCreate: allow('AllowCreate'),
    AllowDelete: allow('AllowDelete'),
    AllowEdit: allow('AllowEdit'),
    AllowRegiezentrum: allow('AllowRegiezentrum'),
    GroupId: 0,
    GroupBezeichnung: null,
    Group: null,
    ViewId: 0,
    ViewBezeichnung: null,
    View: null,
  };
}

/** Erste ViewId zu einer Entität oder `null`. */
export function findViewIdByEntity(
  views: ChinaAppSysView[] | null,
  entity: EntityTypes,
): number | null {
  return views?.find((view) => view.Entity === entity)?.Id ?? null;
}
